import { LmsRouteNames } from '~/types/types';
import type {
   SubscriptionCtaModal,
   SubscriptionCtaModalButtonLinkType,
   SubscriptionCtaModalCopy,
} from '~/types/types';

type SubscriptionCtaContext = 'course' | 'blog' | null;

export type ResolvedSubscriptionCtaModal = SubscriptionCtaModalCopy & {
   routeName: string;
};

// Fallback copy for when the API has not sent site_settings.subscription_cta_modal
// yet. Mirrors the previously hardcoded subscriber wording so untouched portals
// are unaffected.
const FALLBACK_MODAL: SubscriptionCtaModal = {
   course: {
      title: 'Included in your monthly or yearly subscription',
      message:
         'Get access to this and many other courses to level up your polo skills by purchasing a monthly or yearly subscription.',
      buttonText: 'Get a subscription',
      buttonLinkType: 'subscribe',
   },
   blog: {
      title: 'Premium Content',
      message:
         'Get access to this and many other premium blog posts by purchasing a monthly or yearly subscription.',
      buttonText: 'Get a subscription',
      buttonLinkType: 'subscribe',
   },
};

// Semantic key → route name. The API sends the key; the frontend owns routing.
const LINK_TYPE_TO_ROUTE: Record<SubscriptionCtaModalButtonLinkType, string> = {
   subscribe: LmsRouteNames.subscribe,
   memberships: LmsRouteNames.memberships,
};

// Resolves the copy + destination for the global subscription CTA modal.
// Prefers server copy (per-portal member/subscriber variant); falls back to the
// hardcoded subscriber copy when the field is absent. Unknown/absent link types
// default to the subscribe route.
function resolveSubscriptionCtaModal(
   context: SubscriptionCtaContext,
   apiModal?: SubscriptionCtaModal,
): ResolvedSubscriptionCtaModal {
   const resolvedContext = context === 'blog' ? 'blog' : 'course';
   const copy = apiModal?.[resolvedContext] || FALLBACK_MODAL[resolvedContext];
   const routeName =
      LINK_TYPE_TO_ROUTE[copy.buttonLinkType] || LmsRouteNames.subscribe;

   return { ...copy, routeName };
}

export default resolveSubscriptionCtaModal;
